// ---------------------------------------------------------------------------
// Estratto VERBATIM da TabContratti.tsx (Task #3874 — split interno Fase 2).
// Nessuna modifica logica: JSX spostato tale e quale, stato e handler restano
// nel componente padre e arrivano via props esplicite (useToast nel figlio,
// accettato dal mandato).
// ---------------------------------------------------------------------------
import type { Dispatch, SetStateAction } from 'react';
import { useToast } from '@/hooks/use-toast';
import { toErr } from '@easyflow/shared';
import { CreditCard, ChevronDown, Filter, X, Eye, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { CRM_BASE } from '../types';
import type { InstallmentPlanRow } from '../types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterPaymentPlan as PaymentPlan,
  CrmMasterPaymentPlan as PaymentPlanDetail,
} from '@easyflow/shared';
import { Tip, Badge } from '../shared';

type SelectedContract = { quoteId: string; label: string } | null;

interface PaymentPlansSectionProps {
  paymentPlans: PaymentPlan[];
  showPaymentPlans: boolean;
  setShowPaymentPlans: Dispatch<SetStateAction<boolean>>;
  ppStatusFilter: string;
  setPpStatusFilter: Dispatch<SetStateAction<string>>;
  selectedContract: SelectedContract;
  setSelectedContract: Dispatch<SetStateAction<SelectedContract>>;
  setPpDetail: Dispatch<SetStateAction<PaymentPlanDetail | null>>;
  loadContracts: (silent?: boolean) => Promise<void>;
}

export function PaymentPlansSection({
  paymentPlans, showPaymentPlans, setShowPaymentPlans, ppStatusFilter, setPpStatusFilter,
  selectedContract, setSelectedContract, setPpDetail, loadContracts,
}: PaymentPlansSectionProps) {
  const { toast } = useToast();
  return (
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setShowPaymentPlans(!showPaymentPlans)} className="flex items-center gap-2 text-foreground dark:text-white font-semibold text-base">
            <CreditCard className="h-5 w-5 text-blue-400" />
            Piani Rateali ({selectedContract ? paymentPlans.filter((p) => p.quote_id === selectedContract.quoteId).length : paymentPlans.length})
            <ChevronDown className={`h-4 w-4 transition-transform ${showPaymentPlans ? 'rotate-180' : ''}`} />
          </button>
          {paymentPlans.length > 0 && (
            <div className="flex items-center gap-2">
              <select value={ppStatusFilter} onChange={(e) => setPpStatusFilter(e.target.value)}
                className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1 text-xs">
                <option value="">Tutti</option>
                <option value="sent">In attesa</option>
                <option value="accepted">Accettati</option>
                <option value="refused">Rifiutati</option>
              </select>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" /> {paymentPlans.filter((p) => p.status === 'sent').length} in attesa</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" /> {paymentPlans.filter((p) => p.status === 'accepted').length} accettati</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> {paymentPlans.filter((p) => p.status === 'refused').length} rifiutati</span>
              </div>
            </div>
          )}
        </div>
        {selectedContract && (
          <div className="mb-3 flex items-center justify-between gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-lg px-3 py-2">
            <span className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <Filter className="h-3.5 w-3.5" />
              Filtrato per contratto: <strong className="font-semibold">{selectedContract.label || '—'}</strong>
            </span>
            <button onClick={() => setSelectedContract(null)} className="flex items-center gap-1 text-xs text-blue-700 dark:text-blue-300 hover:underline">
              <X className="h-3.5 w-3.5" /> Rimuovi filtro
            </button>
          </div>
        )}
        {showPaymentPlans && (
          <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Cliente</th>
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Preventivo</th>
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Totale</th>
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Rate</th>
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Stato</th>
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Metodo Pagamento</th>
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Contratto</th>
                  <th className="text-center py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Dettagli</th>
                </tr>
              </thead>
              <tbody>
                {paymentPlans.length === 0 ? (
                  <tr><td colSpan={8} className="py-8 text-center text-muted-foreground dark:text-muted-foreground/70">Nessun piano rateale inviato</td></tr>
                ) : paymentPlans
                    .filter((pp) => !ppStatusFilter || pp.status === ppStatusFilter)
                    .filter((pp) => !selectedContract || pp.quote_id === selectedContract.quoteId)
                    .map((pp) => {
                  let plan: InstallmentPlanRow[] = [];
                  try { let raw = typeof pp.installment_plan === 'string' ? JSON.parse(pp.installment_plan) : (pp.installment_plan || []); if (raw && !Array.isArray(raw) && raw.installments) raw = raw.installments; plan = Array.isArray(raw) ? raw : []; } catch { plan = []; }
                  const rateCount = plan.length;
                  return (
                    <tr key={pp.id} className="border-b border-border dark:border-gray-700/50 hover:bg-muted dark:hover:bg-gray-700/50 transition-colors">
                      <td className="py-3 px-4 text-foreground dark:text-white text-sm">{pp.tenant_name || '-'}</td>
                      <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60 text-sm font-mono">{pp.quote_number || '-'}</td>
                      <td className="py-3 px-4 text-foreground dark:text-white text-sm font-semibold">€ {Number(pp.total_amount || pp.quote_total || 0).toFixed(2)}</td>
                      <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60 text-sm">{rateCount} rate / {pp.duration_months || '-'} mesi</td>
                      <td className="py-3 px-4">
                        <Badge className={pp.status === 'accepted' ? 'bg-green-500/20 text-green-400 border-green-500/30' : pp.status === 'refused' ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'}>
                          {pp.status === 'accepted' ? '✓ Accettato' : pp.status === 'refused' ? '✗ Rifiutato' : '⏳ In attesa'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {pp.stripe_pm_brand || pp.stripe_pm_last4 ? (
                          <span className="flex items-center gap-1 text-sm text-foreground dark:text-white">
                            <CreditCard className="h-3.5 w-3.5 text-blue-400" />
                            {pp.stripe_pm_brand || ''} {pp.stripe_pm_last4 ? `••••${pp.stripe_pm_last4}` : ''}
                          </span>
                        ) : pp.stripe_customer_id ? (
                          <span className="text-green-400 text-sm flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" /> Configurato</span>
                        ) : (
                          <span className="text-muted-foreground/70 text-sm">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {pp.contract_number ? (
                          <span className="text-blue-400 text-sm font-mono">{pp.contract_number}</span>
                        ) : pp.status === 'accepted' ? (
                          <span className="text-yellow-400 text-xs">In creazione...</span>
                        ) : (
                          <span className="text-muted-foreground/70 text-sm">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Tip label="Dettagli piano"><button onClick={() => setPpDetail(pp)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-blue-400 p-1"><Eye className="h-4 w-4" /></button></Tip>
                          {!(pp.status === 'accepted' && pp.contract_number) && (
                            <button onClick={async () => {
                              if (!confirm('Eliminare questo piano rateale?')) return;
                              try {
                                await api.delete(`${CRM_BASE}/payment-plans/${pp.id}`);
                                toast({ title: 'Piano eliminato', description: 'Il piano rateale è stato rimosso.' });
                                void loadContracts();
                              } catch (err: unknown) {
                                const __err = toErr(err);
                                toast({ title: 'Errore', description: (__err?.response?.data as { error?: string } | undefined)?.error || 'Impossibile eliminare', variant: 'destructive' });
                              }
                            }} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}
